import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import type { AuditEntryResponse } from '@/domain/models/development.js';
import { formatReasonLine } from '@/presentation/pages/developments/audit/format-reason-line.js';
import { formatDate } from '@/shared/utils/format-date.js';

export interface AuditTabProps {
    entries: AuditEntryResponse[];
    translate: (key: string) => string;
}

export function AuditTab({ entries, translate }: AuditTabProps) {
    return (
        <Card variant="outlined">
            <CardContent>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                    {translate('audit.statusHistory')}
                </Typography>
                {entries.length === 0 ? (
                    <Typography variant="body2" color="text.disabled">
                        {translate('audit.noHistory')}
                    </Typography>
                ) : (
                    <Stack spacing={1}>
                        {entries.map((h) => {
                            const reasonLine = h.type === 'STATUS' ? formatReasonLine(h, translate('documents.reason')) : null;
                            return h.type === 'STATUS' ? (
                                <Box key={h.id}>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        {h.fromStatus && <Chip label={translate(`statusLabels.${h.fromStatus}`)} size="small" variant="outlined" />}
                                        <Typography variant="caption">→</Typography>
                                        {h.toStatus && <Chip label={translate(`statusLabels.${h.toStatus}`)} size="small" />}
                                        <Typography variant="caption" color="text.disabled">
                                            {h.changedByName ?? translate('audit.system')} · {formatDate(h.changedAt)}
                                        </Typography>
                                    </Box>
                                    {reasonLine && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 0.5, mt: 0.5 }}>
                                            {reasonLine}
                                        </Typography>
                                    )}
                                </Box>
                            ) : (
                                <Box key={h.id} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Chip label={h.fieldName} size="small" variant="outlined" />
                                    <Typography variant="caption">
                                        {h.oldValue ?? '—'} → {h.newValue ?? '—'}
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled">
                                        {h.changedByName ?? translate('audit.system')} · {formatDate(h.changedAt)}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
}
